import { INotice, Notifier } from "@airbrake/browser"
import Bowser from "bowser"

import { ENVIRONMENT, IS_DEVELOPMENT, IS_TEST } from "./environment"

// `INoticeError` isn't exported from package so we need to derive it
type INoticeError = NonNullable<INotice["errors"]>[number]

// from config/initializers/airbrake.rb
const PROJECT_ID = 646_847
const PROJECT_KEY = "d12d69aff3b25ad254aa089963731296"

const IGNORED_ERRORS = [
  /^document.getElementsByClassName.ToString is not a function/,
  /^Blocked a frame with origin/,
  /^Calculating structure twice ! Should not happen/,
  // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/29
  /^Object Not Found Matching Id/,
  /^(cancelled|annulé|cancelado)$/, // Safari throws this on aborted fetch request
  /^undefined is not an object \(evaluating '__gCrWeb.edgeTranslate.detectPageState'\)$/ // Ignore GCR errors
]

const IGNORED_FILE_PREFIXES = [
  "chrome-extension://",
  "duckduckgo-privacy-protection.js",
  "file:///Applications/Honey.app",
  "HoneyError",
  "https://config1.veinteractive.com",
  "https://home-l32.niceincontact.com/inContact/ChatClient/js/embed.min.js",
  "safari-extension://",
  "safari-web-extension://",
  "webkit-masked-url://hidden"
]

const IGNORED_EXTENSION_FUNCTIONS = [
  "cancellationExecute" // Honey
]

// Ignore bots not detected by `bowser`
const isBot = (notice: INotice): boolean => {
  const bots = ["AdsBot-Google-Mobile"]
  return bots.some((bot) => notice.context.userAgent.includes(bot))
}

const ignoreError = (error: INoticeError): boolean => {
  return IGNORED_ERRORS.some((ignored) => ignored.test(error.message))
}

const isUsingSupportedBrowser = (): boolean => {
  const browser = Bowser.getParser(window.navigator.userAgent)

  const isInvalidBrowser = browser.satisfies({
    googlebot: ">=0",
    ie: ">=0",
    opera: ">=0"
  })

  if (isInvalidBrowser) {
    return false
  }

  // E.g. https://browsersl.ist/#q=last+2+major+versions%2C+not+dead
  const isValidBrowser = browser.satisfies({
    android: ">=131",
    chrome: ">=130",
    edge: ">=130",
    firefox: ">=132",
    safari: ">=17.6",
    samsung_internet: ">=26"
  })

  // Default to `true` if browser not found, just to catch any weirdness
  return isValidBrowser === undefined ? true : isValidBrowser
}

const ignoreFile = (error: INoticeError): boolean => {
  return IGNORED_FILE_PREFIXES.some((ignored) =>
    error.backtrace?.some((frame) => frame.file?.startsWith(ignored))
  )
}

const isGlobalCodeError = (error: INoticeError): boolean => {
  return error.backtrace?.length === 1 && error.backtrace[0].function === "global code"
}

const isIgnoredOrEmptyFile = (file: string | undefined): boolean => {
  return (
    !file ||
    file === "[native code]" ||
    IGNORED_FILE_PREFIXES.some((prefix) => file.startsWith(prefix))
  )
}

const isInjectedFunctionError = (error: INoticeError): boolean => {
  if (!error.backtrace?.length) {
    return false
  }

  const hasInjectedFunction = error.backtrace.some(
    (frame) => frame.function && IGNORED_EXTENSION_FUNCTIONS.includes(frame.function)
  )

  if (!hasInjectedFunction) {
    return false
  }

  return error.backtrace.every((frame) => isIgnoredOrEmptyFile(frame.file))
}

const airbrakeClient = new Notifier({
  environment: ENVIRONMENT || "production",
  projectId: PROJECT_ID,
  projectKey: PROJECT_KEY,
  remoteConfig: false,
  instrumentation: {
    unhandledrejection: false
  }
})

airbrakeClient.addFilter((notice: INotice): INotice | null => {
  const error = notice.errors && notice.errors[0]
  const shouldIgnore =
    !error ||
    isBot(notice) ||
    window.location.pathname.startsWith("/blog/") ||
    ignoreError(error) ||
    !isUsingSupportedBrowser() ||
    ignoreFile(error) ||
    isGlobalCodeError(error) ||
    isInjectedFunctionError(error)

  if (shouldIgnore) {
    return null
  }

  if (IS_DEVELOPMENT || IS_TEST) {
    console.log(notice) // eslint-disable-line no-console
    return null
  } else {
    return notice
  }
})

export const logDecodingError = (flags: Flags): void => {
  void airbrakeClient.notify({
    error: `Error decoding flags or payload at ${window.location.href}`,
    params: flags
  })
}

export default airbrakeClient
